import first from "lodash/first";
import has from "lodash/has";
import isNil from "lodash/isNil";
import isString from "lodash/isString";
import { EXPIRABLE_CUSTOM_TYPES } from "PFApp/constants/expirable_custom_types";
import { allowsNewValues } from "PFCore/helpers/custom_type";
import { CustomType, PureCustomValueData } from "PFTypes";
import { useMemo } from "react";

type UseCustomValueFieldFlagsParams = {
  customType: CustomType | null;
  multi?: boolean;
  values: PureCustomValueData[];
  letCreate: boolean | null;
  hasValuesWithExpiryDate?: boolean;
};

export const useCustomValueFieldFlags = ({
  customType,
  multi,
  values,
  letCreate,
  hasValuesWithExpiryDate
}: UseCustomValueFieldFlagsParams) => {
  const isMulti = multi ?? customType?.kind === "multiple";
  const isCalendar = customType?.value_type === "date";
  const isBoolean = customType?.value_type === "boolean";

  // When the value is not passed we default to custom type config
  const finalLetCreate = isNil(letCreate) && customType ? allowsNewValues(customType) : letCreate;

  const isChecked = useMemo(() => {
    if (!isBoolean) {
      return false;
    }
    const customValue = (first(values) || {}) as PureCustomValueData;
    // in some cases we receive only "text"
    const value = has(customValue, "value") ? customValue.value : customValue.text;
    return isString(value) ? value === "true" : Boolean(value);
  }, [values, isBoolean]);

  const hasExpiryDate = isNil(hasValuesWithExpiryDate)
    ? EXPIRABLE_CUSTOM_TYPES.includes(customType?.name || "")
    : hasValuesWithExpiryDate;

  return { isMulti, isCalendar, isBoolean, finalLetCreate, isChecked, hasExpiryDate };
};
